import React, { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, Popover, Typography } from '@mui/material';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { KpiCard, KpiGrid, type KpiTone } from '../../../components/ui';
import { tokens } from '../../../theme/tokens';
import type { KpiFlag } from '../commandDashboardTokens';
import { KPI_MAX, type KpiDefinition } from '../kpiCatalog';
import type { KpiCellData } from '../types';
import { LockedFeature } from '../../../components/planLock/LockedFeature';

const { surface, line, ink, accent, signal, radius } = tokens;

interface KpiStripProps {
  /** Every number this page can show right now, each with its catalog `id`. */
  kpis: KpiCellData[];
  loading?: boolean;
  /** The shell's catalog: the picker lists what is not shown in this order. */
  catalog?: readonly KpiDefinition[];
  /** What to show while nothing is saved. Defaults to `kpis` in the order given. */
  defaultOrder?: readonly string[];
  /** The saved list (`config.kpis`). Ids this page cannot show are skipped, never dropped from what `onChange` returns. */
  order?: string[];
  /** Numbers the plan does not include: not drawn, not listed in the picker. */
  lockedIds?: readonly string[];
  /**
   * Plan name for a locked id that should stay visible, greyed out, instead of
   * being omitted. Absent means the id stays hidden (today's behaviour).
   */
  lockedNotices?: Record<string, string | null>;
  /** Dashboard edit mode: reorder, remove and add. */
  editing?: boolean;
  /** The new full list; `removedLabel` is set when the change removed a number (the page offers Undo). */
  onChange?: (ids: string[], removedLabel?: string) => void;
  /** `card` is the vendor home's bordered tiles; `cell` the store/admin hairline strip. */
  variant?: 'cell' | 'card';
  /** Fixed column count from `md` up; pass it when the tiles divide evenly (e.g. 8 tiles in 4 columns). */
  columns?: number;
}

const FLAG_TONE: Record<KpiFlag, KpiTone> = {
  none: 'none',
  red: 'problem',
  amber: 'caution',
  muted: 'muted',
};

const ADD_CELL = 'kpi-add';
const KPI_TYPE = 'kpi';

const valueInk = (flag: KpiFlag): string =>
  flag === 'red' ? signal.problem.ink : flag === 'muted' ? ink.muted : ink.primary;

const renderCard = (kpi: KpiCellData, loading: boolean | undefined, variant: 'cell' | 'card') => (
  <KpiCard
    variant={variant}
    label={kpi.label}
    value={kpi.value}
    delta={kpi.delta}
    note={kpi.note}
    tone={FLAG_TONE[kpi.flag]}
    // The handoff keeps an amber KPI's value in ink and colours only its delta (the vendor cards colour both).
    colorValue={variant === 'card' || kpi.flag !== 'amber'}
    loading={loading}
  />
);

export const KpiStrip: React.FC<KpiStripProps> = ({
  kpis,
  loading,
  catalog,
  defaultOrder,
  order,
  lockedIds,
  lockedNotices,
  editing = false,
  onChange,
  variant = 'cell',
  columns,
}) => {
  const { t } = useTranslation();
  const tt = t as unknown as (key: string, options?: Record<string, unknown>) => string;
  const rootRef = useRef<HTMLDivElement>(null);
  const firstRowRef = useRef<HTMLButtonElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const byId = new Map(kpis.map((k) => [k.id, k]));
  const locked = new Set(lockedIds ?? []);
  const noticeFor = (id: string): string | null | undefined =>
    lockedNotices && Object.prototype.hasOwnProperty.call(lockedNotices, id) ? lockedNotices[id] ?? null : undefined;
  const noticed = (id: string) => noticeFor(id) !== undefined && locked.has(id);
  const drawable = (id: string) => byId.has(id) && !locked.has(id);
  const saved = order ?? (defaultOrder ? [...defaultOrder] : kpis.map((k) => k.id));
  const fromSaved = saved.filter(drawable);
  // A saved list this page can show nothing from (every id locked or unavailable) falls back to the default.
  const shownIds = fromSaved.length > 0 || !defaultOrder ? fromSaved : defaultOrder.filter(drawable);
  // Saved ids that are skipped (plan-locked, or not built for this page) ride along untouched on every change.
  const kept = order ? order.filter((id) => !drawable(id)) : [];
  const shown = shownIds.map((id) => byId.get(id)!);

  const pickable = (catalog ? catalog.map((c) => c.id) : kpis.map((k) => k.id)).filter(
    (id) => drawable(id) && !shownIds.includes(id)
  );
  // A number whose live note is empty still says what it is in the picker.
  const noteFallback = (id: string) => {
    const def = catalog?.find((c) => c.id === id);
    return def ? tt(def.noteKey) : '';
  };
  const canAdd = shownIds.length + kept.length < KPI_MAX;
  const canRemove = shownIds.length > 1;
  // A fixed column count only holds while the tiles (and the Add cell in edit mode) fill whole rows.
  const tileCount = shownIds.length + (editing && canAdd ? 1 : 0);
  const gridColumns = columns && tileCount % columns === 0 ? columns : undefined;

  const commit = (next: string[], removedLabel?: string) => onChange?.([...next, ...kept], removedLabel);
  const remove = (id: string) => {
    if (!canRemove) return;
    commit(shownIds.filter((x) => x !== id), byId.get(id)?.label);
  };
  const add = (id: string) => {
    commit([...shownIds, id]);
    if (shownIds.length + kept.length + 1 >= KPI_MAX) setPickerOpen(false);
  };

  // The picker stays open while several are added; keep keyboard focus on its first row as rows leave.
  useEffect(() => {
    if (pickerOpen) firstRowRef.current?.focus();
  }, [pickerOpen, pickable.length]);
  useEffect(() => {
    if (!editing) setPickerOpen(false);
  }, [editing]);

  // Mouse drags start after 4px so the remove button still clicks; touch (not the mouse sensor) needs a 200ms press so the page scrolls.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const labelOf = (id: unknown) => byId.get(String(id))?.label ?? String(id);
  const positionOf = (id: unknown) => ({ position: shownIds.indexOf(String(id)) + 1, total: shownIds.length });
  const isKpi = (e: { active: { data: { current?: { type?: string } } } }) => e.active.data.current?.type === KPI_TYPE;
  const announcements: Announcements = {
    onDragStart: (e) => (isKpi(e) ? tt('dashboardLayout.announce.pickedUp', { title: labelOf(e.active.id) }) : undefined),
    onDragOver: (e) =>
      isKpi(e) && e.over
        ? tt('dashboardLayout.announce.moved', {
            title: labelOf(e.active.id),
            ...(e.over.id === ADD_CELL ? { position: shownIds.length, total: shownIds.length } : positionOf(e.over.id)),
          })
        : undefined,
    onDragEnd: (e) =>
      isKpi(e) && e.over
        ? tt('dashboardLayout.announce.dropped', {
            title: labelOf(e.active.id),
            ...(e.over.id === ADD_CELL ? { position: shownIds.length, total: shownIds.length } : positionOf(e.over.id)),
          })
        : undefined,
    onDragCancel: (e) => (isKpi(e) ? tt('dashboardLayout.announce.cancelled', { title: labelOf(e.active.id) }) : undefined),
  };

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || !isKpi(e)) return;
    const from = shownIds.indexOf(String(active.id));
    // Dropping on the Add number cell sends the number to the end.
    const to = over.id === ADD_CELL ? shownIds.length - 1 : shownIds.indexOf(String(over.id));
    if (from < 0 || to < 0 || from === to) return;
    commit(arrayMove(shownIds, from, to));
  };

  if (!editing) {
    const viewIds = saved.filter((id) => drawable(id) || noticed(id));
    const shownView = viewIds.length > 0 || !defaultOrder ? viewIds : defaultOrder.filter((id) => drawable(id) || noticed(id));
    return (
      <KpiGrid variant={variant} columns={gridColumns}>
        {shownView.map((id) => {
          const notice = noticeFor(id);
          if (notice !== undefined && locked.has(id)) {
            return <LockedFeature key={id} variant="compact" planName={notice} title={byId.get(id)?.label} />;
          }
          const kpi = byId.get(id);
          if (!kpi) return null;
          return <React.Fragment key={id}>{renderCard(kpi, loading, variant)}</React.Fragment>;
        })}
      </KpiGrid>
    );
  }

  return (
    <Box ref={rootRef} sx={{ position: 'relative' }}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} accessibility={{ announcements }} onDragEnd={onDragEnd}>
        <SortableContext items={shownIds} strategy={rectSortingStrategy}>
          <KpiGrid variant={variant} columns={gridColumns}>
            {shown.map((kpi, index) => (
              <EditableCell
                key={kpi.id}
                id={kpi.id}
                ariaLabel={tt('dashboardLayout.kpis.cellLabel', { label: kpi.label, position: index + 1, total: shown.length })}
                removeLabel={tt('dashboardLayout.kpis.removeLabel', { label: kpi.label })}
                canRemove={canRemove}
                onRemove={() => remove(kpi.id)}
              >
                {renderCard(kpi, loading, variant)}
              </EditableCell>
            ))}
            {canAdd && <AddCell variant={variant} label={tt('dashboardLayout.kpis.add')} open={pickerOpen} onClick={() => setPickerOpen(true)} />}
          </KpiGrid>
        </SortableContext>
      </DndContext>

      <Popover
        open={pickerOpen}
        anchorEl={rootRef.current}
        onClose={() => setPickerOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-label': tt('dashboardLayout.kpis.pickerTitle'),
            sx: {
              width: 300,
              maxWidth: 'calc(100vw - 32px)',
              p: '8px',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: surface.card,
              border: `1px solid ${line.default}`,
              borderRadius: `${radius.card}px`,
              boxShadow: tokens.shadow.menu,
            },
          },
        }}
      >
        <Typography sx={{ p: '6px 8px 4px', fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: ink.muted }}>
          {tt('dashboardLayout.kpis.pickerTitle')}
        </Typography>
        {pickable.length === 0 ? (
          <Typography sx={{ p: '8px', fontSize: 12.5, color: ink.muted }}>{tt('dashboardLayout.kpis.pickerEmpty')}</Typography>
        ) : (
          pickable.map((id, index) => {
            const kpi = byId.get(id)!;
            return (
              <ButtonBase
                key={id}
                ref={index === 0 ? firstRowRef : undefined}
                autoFocus={index === 0}
                onClick={() => add(id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px',
                  p: '8px',
                  textAlign: 'left',
                  borderRadius: `${radius.small}px`,
                  '&:hover, &:focus-visible': { bgcolor: surface.sunken },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: ink.primary }}>{kpi.label}</Typography>
                  <Typography sx={{ fontSize: 12, color: ink.muted }}>{kpi.note || noteFallback(id)}</Typography>
                </Box>
                <Typography sx={{ flex: '0 0 auto', fontSize: 13, fontWeight: 700, color: valueInk(kpi.flag), fontVariantNumeric: 'tabular-nums' }}>
                  {loading ? '' : kpi.value}
                </Typography>
              </ButtonBase>
            );
          })
        )}
      </Popover>
    </Box>
  );
};

interface EditableCellProps {
  id: string;
  ariaLabel: string;
  removeLabel: string;
  canRemove: boolean;
  onRemove: () => void;
  children: React.ReactNode;
}

const EditableCell: React.FC<EditableCellProps> = ({ id, ariaLabel, removeLabel, canRemove, onRemove, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, data: { type: KPI_TYPE } });
  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      role="group"
      tabIndex={0}
      aria-label={ariaLabel}
      data-kpi-id={id}
      onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
        listeners?.onKeyDown?.(e);
        if (!isDragging && (e.key === 'Delete' || e.key === 'Backspace') && e.target === e.currentTarget) {
          e.preventDefault();
          onRemove();
        }
      }}
      sx={{
        position: 'relative',
        zIndex: isDragging ? 2 : 1,
        minWidth: 0,
        cursor: 'grab',
        touchAction: 'manipulation',
        transform: CSS.Translate.toString(transform),
        transition,
        bgcolor: isDragging ? signal.info.row : 'transparent',
        outline: isDragging ? `2px dashed ${signal.info.solid}` : 'none',
        outlineOffset: '-3px',
        '&:hover': { bgcolor: isDragging ? signal.info.row : surface.subtle },
        '&:focus-visible': { outline: `2px solid ${accent.main}`, outlineOffset: '-3px' },
        '& .MuiTypography-overline': { pr: '24px' },
        '& [data-kpi-body]': { opacity: isDragging ? 0.35 : 1 },
        // Links inside a cell do not navigate while editing.
        '& a': { pointerEvents: 'none' },
      }}
    >
      <Box data-kpi-body>{children}</Box>
      {canRemove && !isDragging && (
        <ButtonBase
          aria-label={removeLabel}
          onClick={onRemove}
          // A press or key on the button must not pick the cell up.
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 22,
            height: 22,
            borderRadius: `${radius.chip}px`,
            bgcolor: surface.tint,
            color: ink.bodyAlt,
            '&:hover, &:focus-visible': { bgcolor: signal.problem.tint },
          }}
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
            <path d="M2 2l6 6M8 2L2 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </ButtonBase>
      )}
    </Box>
  );
};

const AddCell: React.FC<{ variant: 'cell' | 'card'; label: string; open: boolean; onClick: () => void }> = ({ variant, label, open, onClick }) => {
  const { setNodeRef, isOver } = useDroppable({ id: ADD_CELL });
  const lit = isOver || open;
  return (
    <ButtonBase
      ref={setNodeRef}
      onClick={onClick}
      aria-haspopup="dialog"
      aria-expanded={open}
      sx={{
        minHeight: 86,
        flexDirection: 'column',
        gap: '6px',
        bgcolor: lit ? signal.info.row : surface.card,
        color: lit ? accent.hover : ink.bodyAlt,
        ...(variant === 'cell'
          ? { borderRight: `1px solid ${line.hairline}`, borderBottom: `1px solid ${line.hairline}` }
          : { border: `1px dashed ${ink.disabled}`, borderRadius: `${radius.card}px` }),
        '&:hover': { bgcolor: signal.info.row, color: accent.hover },
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'inherit' }}>{label}</Typography>
    </ButtonBase>
  );
};
